import { IAppState, initialAppState, NavigationTarget, tabState, WebchatOptions } from "../reduxState";
import { GenericAction, actionTypes } from "../actions";
import { EditorTab } from "../../utilities/types";
import * as merge from 'deepmerge';
import { isPlainObject } from 'is-plain-object';

const simpleUpdateIn = require("simple-update-in");
export type ContainerName = "styleOptions" | "UI" | "root" | "middlewares" | "localization" | "languages" | "UI.headerOptions" | "VoiceUI" | "AvatarUI";
declare const DruidWebchat_v2: any;


export function updateStyleElement(state: IAppState, styleElementName: string, value: any, parentObject: ContainerName = "styleOptions"): IAppState {
  // const parentObject:  ContainerName = "styleOptions";
  // return simpleUpdateIn(state, [parentObject, styleElementName], () => value);

  if (styleElementName === "customDruidStyleSet") {
    try {
      value = JSON.parse(value || "{}");
    } catch {
      return state;
    }
  }

  if (styleElementName === "druidCookiesExpirationInDays") {
    try {
      const parts = value.split('/').map(Number);
      if (parts.length === 2 && parts.every((part: number) => Number.isFinite(part)) && parts[1] !== 0) {
        value = parts[0] / parts[1];
      } else {
        const num = Number(value);
        value = Number.isFinite(num) ? num : 0;
      }
    } catch {
      return state;
    }
  }
  const mergedObj = merge.all<WebchatOptions>([state.webchatOptions, parentObject === "root" ?
    {
      [styleElementName]: value
    }
    :
    parentObject === "UI.headerOptions" ?
      {
        "UI": {
          "headerOptions": {
            [styleElementName]: value
          }
        }
      }
      :
      {
        [parentObject]: {
          [styleElementName]: value
        }
      }
  ], { isMergeableObject: isPlainObject });

  if (styleElementName === "customDruidStyleSet") { // always replace
    mergedObj.customDruidStyleSet = value;
  }

  if (styleElementName === "webChatEventHandler") {
    mergedObj.webChatEventHandlerString = value;
  }

  (async () => {
    const { druidWebChatDispatch } = DruidWebchat_v2.druidWebChatInstance;
    switch (styleElementName) {
      case 'position': {
        return druidWebChatDispatch({
          type: 'SET_SIDE',
          payload: value
        });
      }
      case 'customDruidStyleSet': {
        return druidWebChatDispatch({
          type: 'SET_MERGEDCHATOPTIONS_SIMPLE_VARIABLE', payload: { variablePath: [styleElementName], variableValue: value } //json parsed above
        });
      }
      // case 'druidCookiesExpirationInDays': {
      //   return druidWebChatDispatch({
      //     type: 'SET_MERGEDCHATOPTIONS_SIMPLE_VARIABLE', payload: { variablePath: [styleElementName], variableValue: value} //json parsed above
      //   });
      // }
      case 'webChatEventHandler': {
        return druidWebChatDispatch({
          type: 'SET_MIDDLEWARE_OR_EVENT_HANDLER', payload: { handlerName: [styleElementName], handler: value, withDecorate: true }
        });
      }
      case 'activityStatusMiddleware':
      case 'cardActionMiddleware':
      case 'attachmentMiddleware':
      case 'activityMiddleware':
      case 'scrollToEndButtonMiddleware': {
        return druidWebChatDispatch({
          type: 'SET_MIDDLEWARE_OR_EVENT_HANDLER', payload: { handlerName: [styleElementName], handler: value, withDecorate: false }
        });
      }
      // case 'showChatTitle':
      case 'showCloseButton':
      case 'showVoiceInputSelector':
      case 'showChangeSideButton':
      case 'showLanguageSelector':
      case 'showAvatar':
      case 'showReload':
      case 'showReset': {
        return druidWebChatDispatch({
          type: 'SET_HEADEROPTIONS', payload: { [styleElementName]: value }
        });
      }
      case 'userAvatarImage': {
        // druidWebChatDispatch({
        //   type: 'SET_MERGEDCHATOPTIONS_SIMPLE_VARIABLE', payload: { variablePath: ['styleOptions', 'userAvatarInitials'], variableValue: value ? '': undefined}
        // });
        mergedObj.styleOptions.userAvatarInitials = value ? '' : undefined;
        return druidWebChatDispatch({
          type: 'SET_MERGEDCHATOPTIONS_SIMPLE_VARIABLE', payload: { variablePath: ['styleOptions', 'userAvatarImage'], variableValue: value }
        });
      }
      case 'botAvatarImage': {
        mergedObj.styleOptions.botAvatarInitials = value ? '' : undefined;
        druidWebChatDispatch({
          type: 'SET_MERGEDCHATOPTIONS_SIMPLE_VARIABLE', payload: { variablePath: ['styleOptions', 'botAvatarInitials'], variableValue: value ? '' : undefined }
        });
        return druidWebChatDispatch({
          type: 'SET_MERGEDCHATOPTIONS_SIMPLE_VARIABLE', payload: { variablePath: ['styleOptions', 'botAvatarImage'], variableValue: value }
        });
      }
      case 'avatar': {
        return druidWebChatDispatch({ type: 'RESET' });
      }
      case 'druidNewLayoutEnabled': {
        druidWebChatDispatch({
          type: 'SET_MERGEDCHATOPTIONS_SIMPLE_VARIABLE', payload: { variablePath: [...parentObject.split("."), styleElementName], variableValue: value }
        });
        return druidWebChatDispatch({
          type: 'RESET', payload: { UI: { druidNewLayoutEnabled: value === true } }
        });
      }
      default: {
        return druidWebChatDispatch({
          type: 'SET_MERGEDCHATOPTIONS_SIMPLE_VARIABLE', payload: { variablePath: parentObject ? (parentObject === "root" ? [styleElementName] : [...parentObject.split("."), styleElementName]) : [styleElementName], variableValue: value }
        });
      }
    }
  })();

  return {
    ...state,
    webchatOptions: normalizeWebchatEventHandlerValues(mergedObj)
  }
}

export function updateRootStateVariable(state: IAppState, stateVariableName: string, value: any): IAppState {
  if (stateVariableName === "webchatOptions") {
    return {
      ...state,
      webchatOptions: normalizeWebchatEventHandlerValues(value),
    }
  }

  return {
    ...state,
    [stateVariableName]: value,
  }
}

export function selectEditorTab(state: IAppState, tab: EditorTab): IAppState {
  return {
    ...state,
    ...tabState(tab, state.editorMode),
    navigationTarget: undefined,
  }
}

// Opening a group always leaves search mode, so the group content is shown.
export function selectCustomizationCategory(state: IAppState, tab: EditorTab, category: string): IAppState {
  return {
    ...state,
    activeCategories: { ...state.activeCategories, [tab]: category },
    searchQueries: { ...state.searchQueries, [tab]: '' },
    navigationTarget: undefined,
  }
}

export function setCustomizationSearch(state: IAppState, tab: EditorTab, query: string): IAppState {
  return {
    ...state,
    searchQueries: { ...state.searchQueries, [tab]: query },
    navigationTarget: undefined,
  }
}

export function navigateToEntry(state: IAppState, target: NavigationTarget): IAppState {
  return {
    ...selectCustomizationCategory(state, target.tab, target.category),
    ...tabState(target.tab, state.editorMode),
    navigationTarget: target,
  }
}

export function updateStyleOptions(state: IAppState, webchatOptions: any): IAppState {
  const mergedWebchatOptions = merge.all<WebchatOptions>([state.webchatOptions, webchatOptions], { isMergeableObject: isPlainObject });

  return {
    ...state,
    webchatOptions: normalizeWebchatEventHandlerValues(mergedWebchatOptions),
    initialized: true
  }
}


export const WebChatReducer = (state: IAppState = initialAppState, action: GenericAction<actionTypes, any>): IAppState => {
  console.info(action);

  switch (action.type) {
    case "UPDATE_STYLE_ELEMENT":
      return updateStyleElement(state, action.payload.styleElementName, action.payload.value, action.payload.containerName);
    case "UPDATE_STYLE_OPTIONS":
      return updateStyleOptions(state, action.payload);
    case "UPDATE_ROOT_WEBCHAT_STATE_VARIABlE":
      return updateRootStateVariable(state, action.payload.propertyName, action.payload.value)
    case "SELECT_EDITOR_TAB":
      return selectEditorTab(state, action.payload.tab);
    case "SELECT_CUSTOMIZATION_CATEGORY":
      return selectCustomizationCategory(state, action.payload.tab, action.payload.category);
    case "SET_CUSTOMIZATION_SEARCH":
      return setCustomizationSearch(state, action.payload.tab, action.payload.query);
    case "NAVIGATE_TO_ENTRY":
      return navigateToEntry(state, action.payload);
    default:
      return state;
  }
};

const normalizeWebchatEventHandlerValues = (webchatOptions: any): any => {
  if (!webchatOptions || typeof webchatOptions !== "object") {
    return webchatOptions;
  }

  const normalizedOptions = { ...webchatOptions };

  if (typeof normalizedOptions.webChatEventHandler === "string") {
    normalizedOptions.webChatEventHandlerString = normalizedOptions.webChatEventHandler;
  } else if (typeof normalizedOptions.webChatEventHandler === "function") {
    normalizedOptions.webChatEventHandler = normalizedOptions.webChatEventHandlerString || "";
  } else if ((normalizedOptions.webChatEventHandler === undefined || normalizedOptions.webChatEventHandler === null)
    && typeof normalizedOptions.webChatEventHandlerString === "string") {
    normalizedOptions.webChatEventHandler = normalizedOptions.webChatEventHandlerString;
  }

  return normalizedOptions;
};
